import { useOfferTemplateLines } from "@/hooks/use-product-catalog";
import { PHASE_LABEL, PHASE_ORDER } from "@/components/offer/offer-phase-tokens";
import type { OfferTemplate } from "@/hooks/use-offers";
import type { OfferPhase } from "@/lib/offer-calc";

interface Props {
  template: OfferTemplate;
}

/**
 * Hover-preview for en tilbudsmal (XS/S/M/L) i NewOfferDialog (VIB-663c).
 * Viser hvilke linjer som blir forhåndsutfylt når PL velger størrelsen,
 * gruppert per fase. Sats vises ikke siden tilbudsmal-linjer ikke har
 * fast pris; mengde og enhet (t/d/stk) er det som er konkret tilgjengelig.
 *
 * VIB-730: Etter VIB-701 ligger description/phase/unit på den joinede
 * line_template-relasjonen — ikke flatt på offer_template_lines. Vi leser
 * derfor `line.line_template.{name,phase,default_unit}`.
 */
export function OfferSizeTemplatePreview({ template }: Props) {
  const { data: lines = [], isLoading } = useOfferTemplateLines(template.id);

  const grouped = lines.reduce<Record<string, typeof lines>>((acc, line) => {
    const key = line.line_template.phase;
    if (!acc[key]) acc[key] = [];
    acc[key].push(line);
    return acc;
  }, {});

  // Stabil rekkefølge basert på PHASE_ORDER — Object.entries garanterer
  // ikke rekkefølge på tvers av faser med ulik first-insertion-order.
  const orderedPhases = PHASE_ORDER.filter((p) => grouped[p]?.length);

  return (
    <div className="space-y-2 text-xs">
      <div>
        <div className="font-semibold text-sm leading-tight">{template.name}</div>
        {template.description && (
          <div className="text-[11px] text-muted-foreground">
            {template.description}
          </div>
        )}
      </div>
      {isLoading ? (
        <div className="text-[11px] text-muted-foreground">Laster…</div>
      ) : lines.length === 0 ? (
        <div className="text-[11px] text-muted-foreground">
          Ingen linjer definert i denne malen.
        </div>
      ) : (
        <div className="space-y-1.5">
          {orderedPhases.map((phase) => (
            <div key={phase}>
              <div
                data-phase-header
                className="text-[10px] uppercase tracking-wide text-muted-foreground/80 mb-0.5"
              >
                {PHASE_LABEL[phase as OfferPhase] ?? phase}
              </div>
              <ul className="space-y-0.5">
                {grouped[phase].map((line) => {
                  const unit = line.line_template.default_unit;
                  const qty = Number(line.quantity);
                  return (
                    <li
                      key={line.id}
                      className="flex items-center justify-between gap-2"
                    >
                      <span className="truncate">{line.line_template.name}</span>
                      <span className="tabular-nums text-muted-foreground shrink-0">
                        {qty > 0 ? `${qty} ${unit}` : unit}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
